import { Button, Flex, HStack, Text } from '@chakra-ui/react'
import { useRouter } from 'next/router'
import React from 'react'

import logger from '@/utils/logger'

import RPC from '@/client/rpc'
import { getDashboardUrl } from '@/client/services/url'

import TextWithLines from '@/components/TextWithLines/TextWithLines'
import { useToaster } from '@/components/Toast/useToaster'
import Form from '@/components/UI/Form/Form'
import type { FormInputType, FormValues } from '@/components/UI/Form/types'
import ModalV3 from '@/components/UI/Modal/ModalV3'

const ERROR_MESSAGE_MISMATCH = 'Your entered series name is incorrect'

export default function DeleteSeriesModal({
  show,
  onClose,
  seriesId,
  seriesTitle,
}: {
  show: boolean
  onClose: () => void
  seriesId: number
  seriesTitle: string
}): JSX.Element | null {
  const router = useRouter()
  const toast = useToaster()

  const seriesDeleteInputs: FormInputType[] = React.useMemo(
    () => [
      {
        label: 'Confirm series name',
        placeholder: 'Type your series name here',
        type: 'text',
        name: 'name',
        required: true,
        size: 6,
      },
    ],
    []
  )

  const handleEndSeriesSubmit = React.useCallback(async () => {
    try {
      await RPC.endSeries({
        seriesId,
      })
      toast({
        title: `Successfully deleted series: ${seriesTitle}`,
        status: 'success',
      })
      await router.push(getDashboardUrl())
    } catch (err) {
      logger.warning(`Error deleting Series#${seriesId}`, { err })
      toast({
        title: (
          <TextWithLines text={`${err?.message ?? err}`} maxLines={Number.POSITIVE_INFINITY} />
        ),
        status: 'error',
      })
    }
  }, [toast, seriesTitle, router, seriesId])

  const handleEndSeriesValidate = React.useCallback(
    (values: FormValues) =>
      values.name !== seriesTitle
        ? {
            message: ERROR_MESSAGE_MISMATCH,
            errors: { name: ERROR_MESSAGE_MISMATCH },
          }
        : null,
    [seriesTitle]
  )

  return (
    <ModalV3
      size="xl"
      title="End Series Early"
      open={show}
      onClose={onClose}
      body={() => (
        <Flex direction="column" flex={1} display="flex">
          <Flex direction="column">
            <Text as="span">
              <Text as="span" fontWeight="bold">
                WARNING!
              </Text>{' '}
              You are about to end your series early, which means:
              <ul>
                <li>All future sessions will be deleted.</li>
                <li>No one will be able to join your series again.</li>
                <li>Any series group chat your created will be disabled.</li>
                <li>Any current series members will be notified.</li>
              </ul>
              This action is irreversible and you will not be able to resume your series. To end
              your series, confirm the name of your series exactly how it is spelled:{' '}
              <Text as="span" fontWeight="bold" whiteSpace="pre-wrap">
                {seriesTitle}
              </Text>
            </Text>
          </Flex>
          <Flex direction="row" mt={6}>
            <Form
              name="SeriesSidebarTutorActionsEndSeries"
              blocks={seriesDeleteInputs}
              onSubmit={handleEndSeriesSubmit}
              onValidate={handleEndSeriesValidate}
              FooterComponent={({ submitting }) => (
                <Flex direction="row" mt={9} justifyContent="flex-end">
                  <HStack spacing={3}>
                    <Button isDisabled={submitting} variant="outline" color="fg" onClick={onClose}>
                      Cancel
                    </Button>
                    <Button isLoading={submitting} type="submit" variant="danger">
                      End Series
                    </Button>
                  </HStack>
                </Flex>
              )}
            />
          </Flex>
        </Flex>
      )}
    />
  )
}
